import { useState } from 'react';
import { Card, Text } from '@mantine/core';
import { IconCloud, IconUsers, IconCalendar } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { IProject } from '@/types/Project.model';
import { useStore } from '../../store/Store';
import { getCurrentUserRole, getRolePillStyles } from './index';
import { Project } from '@/types/Project.model';
import { colorWithAlpha } from '@/utils/colorWithAlpha';
import classes from './ProjectCard.module.css';

interface ProjectCardProps {
  project: IProject;
  corporateColor: string;
  onClick?: () => void;
}

function formatProjectDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

export function ProjectCard({ project, corporateColor, onClick }: ProjectCardProps) {
  const { t } = useTranslation();
  const myId = useStore((state) => state.userProfile._id);
  const currentRole = getCurrentUserRole(project, myId);
  const [hovered, setHovered] = useState(false);

  const borderColor = hovered
    ? colorWithAlpha(corporateColor, 0.5)
    : 'var(--mantine-color-default-border)';
  const memberCount = project.projectUsers.length;
  const updatedLabel = formatProjectDate(project.updatedAt);

  return (
    <Card
      shadow={hovered ? 'md' : 'sm'}
      padding={0}
      radius="md"
      withBorder
      className={classes.card}
      style={{ borderColor }}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className={classes.header}>
        <div
          className={classes.iconBox}
          style={{
            backgroundColor: colorWithAlpha(corporateColor, hovered ? 0.14 : 0.09),
            borderColor: colorWithAlpha(corporateColor, 0.2),
            color: corporateColor,
          }}
        >
          <IconCloud size={17} stroke={1.75} />
        </div>

        <div className={classes.headerBody}>
          <Text lineClamp={2} className={classes.title} title={project.name}>
            {project.name}
          </Text>
          <span className={classes.rolePill} style={getRolePillStyles(currentRole, corporateColor)}>
            {currentRole}
          </span>
        </div>
      </div>

      <div className={classes.metaRow}>
        <span className={classes.metaChip} title={t('dashboard.project.projectUpdated')}>
          <IconCalendar size={11} className={classes.metaChipIcon} stroke={1.75} />
          <span className={classes.metaChipValue}>{updatedLabel}</span>
        </span>
        <span className={classes.metaChip} title={t('dashboard.project.projectMembers')}>
          <IconUsers size={11} className={classes.metaChipIcon} stroke={1.75} />
          <span className={classes.metaChipValue}>{memberCount}</span>
        </span>
      </div>
    </Card>
  );
}
